import React, { useEffect } from "react";
import styled from "styled-components";
import { RoundButton } from "../common";

import { useSelector, useDispatch } from "react-redux";
import { closeConfirm } from "../../redux/ConfirmSlice";
import ImgSadFace from "../../assets/images/sad-face.png";

import CloseIcon from "../../assets/images/icons/ic-close.svg";

const Confirm = ({ className }) => {
    const dispatch = useDispatch();
    const {
        isOpen,
        text,
        title,
        hasImage,
        cancelFunc,
        confirmFunc,
        cancelText,
        confirmText,
        closeFunc,
        isSingleButton,
        closeDisabled,
        notCloseOnClickDimmed,
        width,
    } = useSelector((state) => state.confirm);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape" && isOpen) {
                handleCloseConfirm();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, dispatch]);

    const handleCloseConfirm = () => {
        if (closeDisabled) return;
        if (closeFunc) {
            closeFunc();
        }
        dispatch(closeConfirm());
    };

    const clickDimmer = (e) => {
        if (!notCloseOnClickDimmed) {
            handleCloseConfirm();
        }
    };

    const clickContainer = (e) => {
        e.stopPropagation();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Dimmer className={className} onClick={clickDimmer}>
            <ConfirmContainer
                className={"modal-container"}
                onClick={clickContainer}
                $width={width}
            >
                <ConfirmHeaderWrap>
                    {!closeDisabled ? (
                        <button onClick={handleCloseConfirm}>
                            <img src={CloseIcon} alt="confirm close icon" />
                        </button>
                    ) : null}
                </ConfirmHeaderWrap>
                <ConfirmBody $hasImage={hasImage}>
                    <ConfirmBodyContent>
                        {hasImage ? <img src={ImgSadFace} alt="image" /> : null}
                        {title ? <h1>{title}</h1> : null}
                        <p>{text}</p>
                    </ConfirmBodyContent>
                    <ConfirmButtonWrap $width={width}>
                        <BtnWrap>
                            {!isSingleButton ? (
                                <ConfirmButton
                                    type={"outline"}
                                    onClick={cancelFunc || handleCloseConfirm}
                                >
                                    {cancelText}
                                </ConfirmButton>
                            ) : null}
                            <ConfirmButton type={"fill"} onClick={confirmFunc}>
                                {confirmText}
                            </ConfirmButton>
                        </BtnWrap>
                    </ConfirmButtonWrap>
                </ConfirmBody>
            </ConfirmContainer>
        </Dimmer>
    );
};

const ConfirmButtonWrap = styled.div`
    padding: ${(props) => (props.$width ? "16px 0" : "16px 40px")};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ConfirmButton = styled(RoundButton)`
    min-width: 142px;
`;

const ConfirmHeaderWrap = styled.div`
    width: 100%;
    height: 68px;
    background-color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 40px;
    padding-right: 20px;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    flex-direction: row-reverse;
    button {
        width: 68px;
        height: 68px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const Dimmer = styled.div`
    position: fixed;
    z-index: 110;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 20px;
    background-color: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ConfirmContainer = styled.article`
    box-sizing: content-box;
    min-width: 320px;
    max-width: ${(props) => (props.$width ? props.$width : "480")}px;
    width: 100%;
    box-shadow: 0 0 10px rgba(98, 120, 233, 0.1);
    background-color: #ffffff;
    border: 1px solid #cbcbcb;
    border-radius: 10px;
`;

const ConfirmBody = styled.div`
    padding: 20px 40px 16px;
    width: 100%;
    max-height: 500px;
    overflow: auto;
    &::-webkit-scrollbar {
        width: 12px;
    }
    &::-webkit-scrollbar-thumb {
        background: var(--gray-400);
        border-radius: 100px;
        border-right: 4px solid #fff;
        border-left: 4px solid #fff;
        border-top: 2px solid #fff;
        border-bottom: 2px solid #fff;
    }
    &::-webkit-scrollbar-track {
        width: 12px;
        background-color: transparent;
    }
`;

const ConfirmBodyContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 16px;

    img {
        width: 56px;
        display: block;
        margin: 0 auto;
    }

    h1 {
        font-weight: 700;
        font-size: 20px;
        line-height: 28px;
        text-align: center;
        letter-spacing: -0.01em;
        color: #282b39;
        margin-top: 20px;
    }

    p {
        white-space: pre-wrap;
        margin-top: 12px;
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        letter-spacing: -0.01em;
        color: var(--gray-black);
    }
`;

const BtnWrap = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

export default Confirm;
